import React, { useEffect, useRef, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { api, NeuralChatApiError } from "../api/client";
import { useNeuralChatAuth } from "../contexts/AuthContext";
import { Mention, Room } from "../types";
import { getSafeNeuralChatPath } from "../utils/routes";
import { ChatRoot, Content, Main, MutedText } from "../styles";
import { RoomSidebar } from "./RoomSidebar";
import { MentionPanel } from "./MentionPanel";
import { useNeuralChatNotificationRealtime } from "../realtime/client";

export type NeuralChatOutletContext = {
  rooms: Room[];
  refreshRooms: () => Promise<void>;
  mentions: Mention[];
  refreshMentions: () => Promise<void>;
  memberId: string;
};

export const NeuralChatShell: React.FC = () => {
  const auth = useNeuralChatAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [rooms, setRooms] = useState<Room[]>([]);
  const [mentions, setMentions] = useState<Mention[]>([]);
  const [roomError, setRoomError] = useState("");
  const [mentionError, setMentionError] = useState("");
  const [roomsLoading, setRoomsLoading] = useState(true);
  const [mentionsLoading, setMentionsLoading] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [mentionsOpen, setMentionsOpen] = useState(false);
  const roomsBusy = useRef(false);
  const mentionsBusy = useRef(false);
  const roomsAbort = useRef<AbortController | null>(null);
  const mentionsAbort = useRef<AbortController | null>(null);

  const refreshRooms = async () => {
    if (roomsBusy.current || document.hidden) return;
    roomsBusy.current = true;
    roomsAbort.current?.abort();
    const controller = new AbortController();
    roomsAbort.current = controller;
    try {
      const next = await api.listRooms(controller.signal);
      setRooms(next);
      setRoomError("");
    } catch (reason) {
      if (reason instanceof NeuralChatApiError && reason.status === 401) {
        auth.markUnauthenticated();
        navigate(`/neuralchat/login?next=${encodeURIComponent(getSafeNeuralChatPath(location.pathname))}`, { replace: true });
      } else if (!(reason instanceof DOMException && reason.name === "AbortError")) {
        setRoomError("채널 목록을 불러오지 못했습니다.");
      }
    } finally {
      roomsBusy.current = false;
      setRoomsLoading(false);
    }
  };

  const refreshMentions = async () => {
    if (mentionsBusy.current || document.hidden) return;
    mentionsBusy.current = true;
    mentionsAbort.current?.abort();
    const controller = new AbortController();
    mentionsAbort.current = controller;
    setMentionsLoading(true);
    try {
      setMentions(await api.unreadMentions(controller.signal));
      setMentionError("");
    } catch (reason) {
      if (reason instanceof NeuralChatApiError && reason.status === 401) auth.markUnauthenticated();
      else if (!(reason instanceof DOMException && reason.name === "AbortError")) setMentionError("멘션을 불러오지 못했습니다.");
    } finally {
      mentionsBusy.current = false;
      setMentionsLoading(false);
    }
  };

  useNeuralChatNotificationRealtime(Boolean(auth.member), {
    onMention: () => { void refreshMentions(); },
    onRoom: () => { void refreshRooms(); },
    onAuthorizationLost: (code) => {
      if (code === 4401) auth.markUnauthenticated();
      else {
        void refreshRooms();
        void refreshMentions();
      }
    },
  });

  useEffect(() => {
    void refreshRooms();
    void refreshMentions();
    const timer = window.setInterval(() => {
      void refreshRooms();
      void refreshMentions();
    }, 5000);
    const onVisibility = () => {
      if (!document.hidden) {
        void refreshRooms();
        void refreshMentions();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisibility);
      roomsAbort.current?.abort();
      mentionsAbort.current?.abort();
    };
    // The shell is mounted only for one authenticated session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => setSidebarOpen(false), [location.pathname]);

  useEffect(() => {
    const openSidebar = () => setSidebarOpen(true);
    window.addEventListener("neuralchat-open-sidebar", openSidebar);
    return () => window.removeEventListener("neuralchat-open-sidebar", openSidebar);
  }, []);

  useEffect(() => {
    const openMentions = () => setMentionsOpen(true);
    window.addEventListener("neuralchat-open-mentions", openMentions);
    return () => window.removeEventListener("neuralchat-open-mentions", openMentions);
  }, []);

  if (!auth.member) return null;
  const context: NeuralChatOutletContext = {
    rooms,
    refreshRooms,
    mentions,
    refreshMentions,
    memberId: auth.member.id,
  };

  return (
    <ChatRoot>
      <RoomSidebar
        rooms={rooms}
        member={auth.member}
        selectedRoomId={location.pathname.startsWith("/neuralchat/rooms/") ? location.pathname.split("/")[3] : undefined}
        open={sidebarOpen}
        loading={roomsLoading}
        error={roomError}
        unreadRoomIds={mentions.map((mention) => mention.room.id)}
        onClose={() => setSidebarOpen(false)}
        onRefresh={refreshRooms}
      />
      <Main>
        <Content><Outlet context={context} /></Content>
      </Main>
      <MentionPanel
        open={mentionsOpen}
        mentions={mentions}
        loading={mentionsLoading}
        error={mentionError}
        onClose={() => setMentionsOpen(false)}
        onSelect={(mention) => {
          setMentionsOpen(false);
          navigate(`/neuralchat/rooms/${mention.room.id}`);
        }}
      />
      <button
        type="button"
        aria-label="읽지 않은 멘션 열기"
        onClick={() => setMentionsOpen(true)}
        style={{ position: "fixed", right: 18, bottom: 18, zIndex: 18, borderRadius: 999, padding: "10px 13px", color: "white", background: "#4f46e5", fontWeight: 800 }}
      >
        멘션 {mentions.length ? `(${mentions.length})` : ""}
      </button>
      {roomError && !rooms.length && <MutedText aria-live="polite">{roomError}</MutedText>}
    </ChatRoot>
  );
};
